import type { EligibilityCheck } from "./eligibilityApi";
import {
  eligibilityDisclaimer,
  eligibilityFields,
  outcomeLabel,
} from "./eligibilityTypes";
import "./eligibility.css";
export function EligibilityResult({
  check,
  showInputs = false,
  showReview = true,
}: {
  check: EligibilityCheck;
  showInputs?: boolean;
  showReview?: boolean;
}) {
  return (
    <div className="eligibility-workspace eligibility-result">
      <section
        className={`eligibility-panel eligibility-outcome ${check.result.outcome === "CRITERIA_MET" ? "is-met" : ""}`}
        aria-label="Saved eligibility result"
        data-outcome={check.result.outcome}
      >
        <span className="eligibility-eyebrow">
          Saved check · {new Date(check.createdAt).toLocaleDateString()}
        </span>
        <h2>{outcomeLabel[check.result.outcome]}</h2>
        <p>{check.result.reason}</p>
        <p className="eligibility-disclaimer">{eligibilityDisclaimer}</p>
      </section>
      <section className="eligibility-panel" aria-label="Criteria breakdown">
        <h2>Criteria breakdown</h2>
        {!check.criteriaId && (
          <p className="text-muted">
            No approved criteria were available for this intake. An admissions
            review is required.
          </p>
        )}
        {check.policySnapshot?.sourceTitle && (
          <p className="small text-muted">
            Criteria version {check.criteriaId} ·{" "}
            {check.policySnapshot.sourceTitle}
            <br />
            {check.policySnapshot.sourceReference}
            <br />
            Effective {check.policySnapshot.effectiveFrom} to{" "}
            {check.policySnapshot.effectiveUntil}
          </p>
        )}
        <ul className="eligibility-criteria">
          {check.result.criteria.map((criterion) => (
            <li key={criterion.id}>
              <div>
                <strong>{criterion.label}</strong>
                <p>{criterion.reason}</p>
              </div>
              <span
                className={`eligibility-state state-${criterion.state.toLowerCase()}`}
              >
                {
                  {
                    MET: "Met",
                    NOT_MET: "Not met",
                    UNKNOWN: "Needs review",
                    ADVISORY: "Advisory",
                  }[criterion.state]
                }
              </span>
            </li>
          ))}
        </ul>
      </section>
      {showInputs && (
        <section className="eligibility-panel">
          <h2>Student information at check</h2>
          <p className="text-muted small">
            Saved facts are self-reported until verified by admissions.
          </p>
          <dl className="eligibility-facts">
            <div>
              <dt>Intake</dt>
              <dd>{check.intake}</dd>
            </div>
            {eligibilityFields.map((spec) => (
              <div key={spec.field}>
                <dt>{spec.label}</dt>
                <dd>{check.inputs[spec.field] ?? "Not provided"}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      {showReview && <EligibilityReviewResponse check={check} />}
    </div>
  );
}

export function EligibilityReviewResponse({
  check,
}: {
  check: EligibilityCheck;
}) {
  const review = check.review;
  const decision = review?.decisionOutcome;
  return (
    <>
      {review && (
        <section className="eligibility-panel" aria-label="Review response">
          <h2>Eligibility review #{review.id}</h2>
          <p>
            <strong>
              {(
                {
                  OPEN: "Awaiting reviewer",
                  IN_REVIEW: "Under review",
                  DECIDED: "Response received",
                } as Record<string, string>
              )[review.status] || review.status}
            </strong>
            {review.status === "OPEN" &&
              " · Your request is in the review queue."}
          </p>
          <p className="small">{review.message}</p>
          {decision && (
            <div
              className={`eligibility-review-decision decision-${decision.toLowerCase()}`}
            >
              <span className="eligibility-decision-label">
                Reviewer response
              </span>
              <h3 className="eligibility-decision-outcome">
                {outcomeLabel[decision]}
              </h3>
              <p className="eligibility-decision-note">{review.decisionNote}</p>
              {review.decisionValidUntil && (
                <p className="small mb-0">
                  Valid through {review.decisionValidUntil.slice(0, 10)} (UTC),
                  subject to current course access and unchanged criteria.
                </p>
              )}
            </div>
          )}
          <ol className="eligibility-history">
            {review.events.map((event, index) => (
              <li key={index}>
                {event.type.replace(/_/g, " ").toLowerCase()} ·{" "}
                {new Date(event.at).toLocaleString()}
                {event.note && <p>{event.note}</p>}
              </li>
            ))}
          </ol>
        </section>
      )}
    </>
  );
}
